import {
  Box,
  Button,
  ButtonSize,
  Checkbox,
  Icon,
  IconSize,
  ModalBody,
  Radio,
  Separator,
  SeparatorType,
  Text,
  TextFieldSize,
} from '@bechtel/gds';
import {
  CableEnd,
  CableToEnd,
  Equipment,
  EquipmentBlockPoint,
  GroundLocation,
  OperatorType,
  TenantConfig,
  TermBlockSide,
} from '@bechtel/sr-types';
import { GridApi } from 'ag-grid-community';
import { Formik } from 'formik';
import { filter, find, first, forEach, get, map, slice, uniq } from 'lodash';
import { FC, useCallback, useRef, useState } from 'react';
import { client } from '~/apollo-client';
import {
  AppSpinner,
  FormField,
  FormSectionHeading,
  MultiLingualText,
  TextFieldComponent,
  useAppUser,
} from '~/components';
import {
  GET_EQUIPMENT_CODE_BY_EQUIPMENT_IDS,
  GET_FIRST_EQUIPMENT_TAG_IN_LOOP,
  SEARCH_CABLE_FROM_ENDS,
  SEARCH_CABLE_TO_ENDS,
  SEARCH_EQUIPMENT_CODE_BY_EQUIPMENT_TAG,
} from '~/gql';
import { EquipmentBlockPointList } from '../equipment-block-point-list';
import { EquipmentCableList } from '../equipment-cable-list';
import { EquipmentCable, TermEnd, TermItem } from '../types';
import {
  AutoTerminateModalStyled,
  BoxContainerStyled,
  FooterButtonStyled,
  LineStyled,
  ListSelectionStyled,
  ModalCloseStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
} from './styled';

const initialValues = {
  autoTermOption: TermEnd.BOTH_FROM_AND_TO,
  terminateToShieldBar: false,
  shieldBlockTag: '',
  shieldPointTag: '',
  termimateShieldWiresAtBothEnds: false,
  terminateToGroundBar: false,
  groundBlockTag: '',
  groundPointTag: '',
  termimategroundWiresAtBothEnds: false,
};

interface AutoTerminateModalProps {
  toEquipmentId: string;
  equipmentTag: string;
  onClose: () => void;
  equipmentCables: EquipmentCable[];
  termList: TermItem[];
  onAutoTerm: (termList: TermItem[], changedCommodityIds: string[]) => void;
  fromEquipmemtBlockStatMap: Map<string, { cNo: number; vNo: number }>;
  toEquipmentBlockStatMap: Map<string, { cNo: number; vNo: number }>;
}

//
// search for any non-blank block/point; if any are found, or if the cable is prepared,
// then the cable has already been terminated to some extent and must be skipped
//
const checkTermination = (foundTermList: TermItem[]): boolean => {
  let canTerminate: boolean = true;

  forEach(foundTermList, (termItem: TermItem) => {
    // check for change control engineering status
    if (Number(termItem.fromTermEngrStatus) > 0 || Number(termItem.toTermEngrStatus) > 0) {
      canTerminate = false;
      return canTerminate;
    }
    // check for from/to end non empty block and points
    if (termItem.fromBlockTag || termItem.fromPointTag || termItem.toBlockTag || termItem.toPointTag) {
      canTerminate = false;
      return canTerminate;
    }
  });
  return canTerminate;
};

//
// Get next valid block point,
//
const getNextValidEquipmentBlockPoint = (
  equipmentBlockPointList: EquipmentBlockPoint[],
  lastIndex: number,
  cableEnd: CableEnd
): EquipmentBlockPoint | null => {
  let equipmentBlockPoint: any = null;

  forEach(slice(equipmentBlockPointList, lastIndex), (blockPoint: EquipmentBlockPoint) => {
    const termCount = cableEnd === CableEnd.FROM ? blockPoint.customerCount : blockPoint.vendorCount || 0;
    // check if no points are terminated there
    if (termCount === 0) {
      equipmentBlockPoint = blockPoint;
      return false;
    }
  });
  return { ...equipmentBlockPoint };
};

const findAndIncrementTermCounter = (
  equipmemtBlockStatMap: Map<string, { cNo: number; vNo: number }>,
  equipmentId: string | null,
  blockTag: string | null,
  pointTag: string | null,
  termBlockSide: TermBlockSide
) => {
  if (!equipmemtBlockStatMap || equipmemtBlockStatMap.size === 0 || !(equipmentId || blockTag || pointTag)) return;
  const mapKey = `${equipmentId}-${blockTag}-${pointTag}`;
  const equipmentBlockStat = equipmemtBlockStatMap.get(mapKey);
  if (equipmentBlockStat) {
    if (termBlockSide === TermBlockSide.CUSTOMER) {
      equipmentBlockStat.cNo = (equipmentBlockStat.cNo || 0) + 1;
    } else {
      equipmentBlockStat.vNo = (equipmentBlockStat.vNo || 0) + 1;
    }
  }
  // const foundBlockPoint = find(
  //   equipmentBlockPointList,
  //   (equipmentBlockPoint: EquipmentBlockPoint) =>
  //     equipmentBlockPoint.equipmentId === equipmentId &&
  //     equipmentBlockPoint.blockTag === blockTag &&
  //     equipmentBlockPoint.pointTag === pointTag
  // );

  // if (foundBlockPoint) {
  //   incrementTermCounter(foundBlockPoint, termBlockSide);
  // }
};

const getFromEquipmentMap = async (fromEquipmentIds: string[]): Promise<Map<string, boolean>> => {
  const fromEquipmentMap = new Map<string, boolean>();

  forEach(fromEquipmentIds, (fromEquipmentId) => {
    fromEquipmentMap.set(fromEquipmentId, true);
  });
  const searchCableToEndsResult = await client.query({
    query: SEARCH_CABLE_TO_ENDS,
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              field: 'equipmentId',
              operator: OperatorType.IN,
              values: fromEquipmentIds,
            },
          ],
        },
      },
    },
  });

  if (
    searchCableToEndsResult?.data.searchCableToEnds &&
    searchCableToEndsResult?.data.searchCableToEnds.items &&
    searchCableToEndsResult?.data.searchCableToEnds.items.length
  ) {
    forEach(fromEquipmentIds, (fromEquipmentId) => {
      const foundFromEquipmentId = find(
        searchCableToEndsResult?.data.searchCableToEnds.items,
        (cableToEnd: CableToEnd) => cableToEnd.equipmentId === fromEquipmentId
      );
      if (!foundFromEquipmentId) {
        fromEquipmentMap.set(fromEquipmentId, true);
      } else {
        fromEquipmentMap.set(fromEquipmentId, false);
      }
    });
  }
  return fromEquipmentMap;
};

const getToEquipmentMap = async (toEquipmentId: string): Promise<Map<string, boolean>> => {
  const toEquipmentMap = new Map<string, boolean>();
  toEquipmentMap.set(toEquipmentId, false);

  const searchCableFromEndsResult = await client.query({
    query: SEARCH_CABLE_FROM_ENDS,
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              field: 'equipmentId',
              operator: OperatorType.EQUAL,
              value: toEquipmentId,
            },
          ],
        },
      },
    },
  });
  if (
    !(
      searchCableFromEndsResult.data.searchCableFromEnds &&
      searchCableFromEndsResult.data.searchCableFromEnds.items &&
      searchCableFromEndsResult.data.searchCableFromEnds.items.length
    )
  ) {
    toEquipmentMap.set(toEquipmentId, true);
  }

  return toEquipmentMap;
};

const getFirstEquipmentInLoop = async (
  equipmentTag: string,
  blockTag: string,
  pointTag: string
): Promise<string | null> => {
  const result = await client.query({
    query: GET_FIRST_EQUIPMENT_TAG_IN_LOOP,
    variables: {
      equipmentTag: equipmentTag,
      blockTag: blockTag,
      pointTag: pointTag,
    },
  });
  return result.data.firstEquipmentTagInLoop || null;
};

const getEquipmentsGroundLocationMap = async (fromEquipmentIds: string[]): Promise<Map<string, string>> => {
  const equipmentGroundLocationMap = new Map<string, string>();

  const equipmentsResult = await client.query({
    query: GET_EQUIPMENT_CODE_BY_EQUIPMENT_IDS,
    variables: {
      ids: fromEquipmentIds,
    },
  });

  if (equipmentsResult?.data.equipmentByIds && equipmentsResult?.data.equipmentByIds.length) {
    forEach(fromEquipmentIds, (fromEquipmentId) => {
      const foundFromEquipment = find(
        equipmentsResult?.data.equipmentByIds,
        (equipment: Equipment) => equipment.id === fromEquipmentId
      );
      if (
        !foundFromEquipment ||
        !foundFromEquipment.equipmentCode ||
        !foundFromEquipment.equipmentCode.defaultGroundLocation
      ) {
        equipmentGroundLocationMap.set(fromEquipmentId, GroundLocation.B);
      } else {
        equipmentGroundLocationMap.set(fromEquipmentId, foundFromEquipment.equipmentCode.defaultGroundLocation);
      }
    });
  }

  return equipmentGroundLocationMap;
};

const getEquipmentsGroundLocationByTag = async (equipmentTag: string): Promise<string | null> => {
  let equipmentGroundLocation: string | null = null;

  const equipmentsResult = await client.query({
    query: SEARCH_EQUIPMENT_CODE_BY_EQUIPMENT_TAG,
    variables: {
      searchInput: {
        filterInput: {
          and: {
            field: 'tag',
            operator: OperatorType.EQUAL,
            value: equipmentTag,
          },
        },
      },
    },
  });

  if (equipmentsResult?.data?.searchEquipments && equipmentsResult?.data?.searchEquipments?.items?.length) {
    const equipment: Equipment = first(equipmentsResult.data.searchEquipments?.items) as Equipment;
    if (equipment && equipment?.equipmentCode) {
      equipmentGroundLocation = equipment.equipmentCode?.defaultGroundLocation || GroundLocation.B;
    }
  }

  return equipmentGroundLocation;
};

export const AutoTerminateModal: FC<AutoTerminateModalProps> = ({
  onClose,
  equipmentCables,
  toEquipmentId,
  equipmentTag,
  termList,
  onAutoTerm,
  fromEquipmemtBlockStatMap,
  toEquipmentBlockStatMap,
}) => {
  const { userAccount } = useAppUser();
  const tenantConfig: TenantConfig = get(userAccount, 'tenantConfig') as TenantConfig;
  const [equipmentCableListGridApi, setequipmentCableListGridApi] = useState<GridApi>();
  const [fromBlockGridApi, setFromBlockGridApi] = useState<GridApi>();
  const [toBlockGridApi, setToBlockGridApi] = useState<GridApi>();
  const termListRef = useRef<TermItem[]>(structuredClone(termList));
  const [progress, setProgress] = useState(false);

  const fromEquipmentIds: string[] = uniq(
    map(equipmentCables, (equipmentCable: EquipmentCable) => equipmentCable.fromEquipmentId)
  );

  const handleGridReady = (api: GridApi) => {
    setequipmentCableListGridApi(api);
  };

  const handleFromBlockGridReady = (api: GridApi) => {
    setFromBlockGridApi(api);
  };

  const handleToBlockGridReady = (api: GridApi) => {
    setToBlockGridApi(api);
  };

  const getSelectedRows = useCallback(() => {
    if (!equipmentCableListGridApi) return;
    return equipmentCableListGridApi.getSelectedRows();
  }, [equipmentCableListGridApi]);

  const getFromBlockSelectedRows = useCallback(() => {
    if (!fromBlockGridApi) return;
    return fromBlockGridApi.getSelectedRows();
  }, [fromBlockGridApi]);

  const getToBlockSelectedRows = useCallback(() => {
    if (!toBlockGridApi) return;
    return toBlockGridApi.getSelectedRows();
  }, [toBlockGridApi]);

  const handleSubmit = async (values) => {
    if (!equipmentCableListGridApi || !termList) return;
    const shieldDesignator = tenantConfig.shieldDesignator;
    const groundDesignator = tenantConfig.groundDesignator;
    // get all the selected cables
    const selectedCables = getSelectedRows();
    if (!selectedCables || selectedCables.length === 0) return;
    const isSameFromTo = !!find(selectedCables, (selectedCable) => selectedCable.fromEquipmentId === equipmentId);

    const changedTermIds: string[] = [];
    let equipmentId = null;
    let commodityTag = null;
    let isCableOrJumper = null;
    let fromBlockTag: string | null = null;
    let fromPointTag: string | null = null;
    let toBlockTag: string | null = null;
    let toPointTag: string | null = null;
    let canTerminate: boolean = false;
    let wireColor: string | null = null;
    let isShieldWire: boolean = false;
    let isGroundWire: boolean = false;
    let groundLocation: string | null = null;
    let firstGroundLocation: string | null = null;
    let isFromFirst: boolean = false;
    let isToLast: boolean = false;
    //
    setProgress(true);

    // get all cable from ends for to equipment
    const toEquipmentMap: Map<string, boolean> = await getToEquipmentMap(toEquipmentId);

    // get all cable to ends for from equipment
    const fromEquipmentMap: Map<string, boolean> = await getFromEquipmentMap(fromEquipmentIds);

    // get all equipment and equipment code default ground location
    const equipmentGroundLocationMap: Map<string, string> = await getEquipmentsGroundLocationMap(fromEquipmentIds);

    // get all the TO block points for the to equipment
    const toEquipmentBlockPoints: EquipmentBlockPoint[] = filter(
      getToBlockSelectedRows(),
      (equipmentBlockPoint: EquipmentBlockPoint) => equipmentBlockPoint.equipmentId === toEquipmentId
    );
    let toEquipmentBlockPointIndex = toEquipmentBlockPoints && toEquipmentBlockPoints.length ? 0 : -1;

    for (const selectedCable of selectedCables) {
      //
      // equip/cable is selected; terminate all wires to all available points
      //
      equipmentId = selectedCable.fromEquipmentId;
      commodityTag = selectedCable.tag;
      isCableOrJumper = selectedCable.isCableOrJumper;
      // get the ground location, loop end status
      isToLast = toEquipmentMap.get(toEquipmentId) || false;
      isFromFirst = fromEquipmentMap.get(equipmentId || '') || false;
      groundLocation = equipmentGroundLocationMap.get(equipmentId || '') || GroundLocation.B;

      const foundTermList = filter(
        termListRef.current,
        (termItem: TermItem) => termItem.tag === commodityTag && termItem.isCableOrJumper === isCableOrJumper
      );
      //
      // search for any non-blank block/point; if any are found, or if the cable is prepared,
      // then the cable has already been terminated to some extent and must be skipped
      //
      canTerminate = checkTermination(foundTermList);

      if (canTerminate) {
        // get all the FROM block points for the equipment
        const fromEquipmentBlockPoints: EquipmentBlockPoint[] = filter(
          getFromBlockSelectedRows(),
          (equipmentBlockPoint: EquipmentBlockPoint) => equipmentBlockPoint.equipmentId === equipmentId
        );

        let fromEquipmentBlockPointIndex = fromEquipmentBlockPoints && fromEquipmentBlockPoints.length ? 0 : -1;
        //
        // loop through wires in the cable and terminate each unterminated wire to a block
        // with no terms
        //
        for (const termItem of foundTermList) {
          // get wire color
          wireColor = termItem.wireColor;
          isShieldWire =
            wireColor && shieldDesignator
              ? wireColor?.substring(0, shieldDesignator.length) === shieldDesignator
              : false;
          isGroundWire =
            wireColor && groundDesignator
              ? wireColor?.substring(0, groundDesignator.length) === groundDesignator
              : false;
          //
          // get the ID, block, point, and side of FROM  *********************
          //
          if (values.terminateToShieldBar && values.termimateShieldWiresAtBothEnds && isShieldWire) {
            //
            // using shield bars and wire is a shield and both ends are to be terminated to
            // shield bar
            //
            if (values.autoTermOption === TermEnd.TO_ONLY) {
              // only TO terms wanted
              fromBlockTag = null;
              fromPointTag = null;
            } else {
              fromBlockTag = values.shieldBlockTag;
              fromPointTag = values.shieldPointTag;
            }
          } else if (values.terminateToGroundBar && values.termimategroundWiresAtBothEnds && isGroundWire) {
            //
            // using ground bars and wire is a ground and both ends are to be terminated to
            // ground bar
            //
            if (values.autoTermOption === TermEnd.TO_ONLY) {
              // only TO terms wanted
              fromBlockTag = null;
              fromPointTag = null;
            } else {
              fromBlockTag = values.groundBlockTag;
              fromPointTag = values.groundPointTag;
            }
          } else {
            //
            // if there are more wires than available points or if you only want TO terms,
            // then stop; else get point data and check shield status
            //
            if (
              values.autoTermOption === TermEnd.TO_ONLY ||
              fromEquipmentBlockPointIndex >= fromEquipmentBlockPoints.length
            ) {
              fromBlockTag = null;
              fromPointTag = null;
            } else {
              if (
                (isShieldWire || isGroundWire) &&
                (groundLocation === GroundLocation.D || groundLocation === GroundLocation.N) &&
                isFromFirst
              ) {
                //
                // if wire is (a shield OR a ground) AND if it's either (grounded at the
                // destination OR not grounded at all) AND if the wire has no upstream
                // connection (first in loop), then leave wire untermed; i.e., you are at the
                // beginning of the loop
                //
                fromBlockTag = null;
                fromPointTag = null;
              } else {
                if (fromEquipmentBlockPointIndex !== -1) {
                  const foundEquipmentBlockPoint = getNextValidEquipmentBlockPoint(
                    fromEquipmentBlockPoints,
                    fromEquipmentBlockPointIndex,
                    CableEnd.FROM
                  );
                  if (foundEquipmentBlockPoint) {
                    fromBlockTag = foundEquipmentBlockPoint.blockTag || null;
                    fromPointTag = foundEquipmentBlockPoint.pointTag || null;

                    // increment the term counter
                    findAndIncrementTermCounter(
                      fromEquipmemtBlockStatMap,
                      equipmentId,
                      fromBlockTag,
                      fromPointTag,
                      TermBlockSide.CUSTOMER
                    );

                    if (isSameFromTo) {
                      findAndIncrementTermCounter(
                        toEquipmentBlockStatMap,
                        equipmentId,
                        fromBlockTag,
                        fromPointTag,
                        TermBlockSide.CUSTOMER
                      );
                    }
                    fromEquipmentBlockPointIndex += 1;
                  } else {
                    fromBlockTag = null;
                    fromPointTag = null;
                  }
                } else {
                  fromBlockTag = null;
                  fromPointTag = null;
                }
              }
            }
          }
          //
          // get the ID, block, point, and side of TO  *********************
          //
          if (values.terminateToShieldBar && isShieldWire) {
            //
            // using shield bars and wire is a shield and both ends are to be terminated to
            // shield bar
            //
            if (values.autoTermOption === TermEnd.FROM_ONLY) {
              // only FROM terms wanted
              toBlockTag = null;
              toPointTag = null;
            } else {
              toBlockTag = values.shieldBlockTag;
              toPointTag = values.shieldPointTag;
            }
          } else if (values.terminateToGroundBar && isGroundWire) {
            //
            // using ground bars and wire is a ground and both ends are to be terminated to
            // ground bar
            //
            if (values.autoTermOption === TermEnd.FROM_ONLY) {
              // only TO terms wanted
              toBlockTag = null;
              toPointTag = null;
            } else {
              toBlockTag = values.groundBlockTag;
              toPointTag = values.groundPointTag;
            }
          } else {
            //
            // if there are more wires than available points or if you only want FROM terms,
            // then stop; else get point data and check shield status
            //
            if (
              values.autoTermOption === TermEnd.FROM_ONLY ||
              toEquipmentBlockPointIndex >= toEquipmentBlockPoints.length
            ) {
              toBlockTag = null;
              toPointTag = null;
            } else {
              //
              // find out the grounding scheme as defined by the first equip in the loop and
              // get the wire tag so it can be propagated (wire tag is not needed by client)
              //
              if (fromBlockTag && fromPointTag) {
                if (isFromFirst) {
                  firstGroundLocation = groundLocation;
                } else {
                  const firstEquipmentInLoop = await getFirstEquipmentInLoop(
                    selectedCable.fromEquipmentTag,
                    fromBlockTag,
                    fromPointTag
                  );
                  if (firstEquipmentInLoop) {
                    firstGroundLocation = await getEquipmentsGroundLocationByTag(firstEquipmentInLoop);
                  }
                }
              } else {
                //
                // since no FROM block/pt, cannot detect ground location of upstream
                // component; so, default to both unless FROM is first and TO is last (in
                // which case default to FROM's setting)
                //
                if (isFromFirst && isToLast) {
                  firstGroundLocation = groundLocation;
                } else {
                  firstGroundLocation = GroundLocation.B;
                }
              }
              //
              // get TO block and point
              //
              if (
                (isShieldWire || isGroundWire) &&
                (firstGroundLocation === GroundLocation.O || firstGroundLocation === GroundLocation.N) &&
                isToLast
              ) {
                //
                // if wire is (a shield OR a ground) AND if it's either (grounded at the
                // origin OR not grounded at all) AND if the wire has no downstream
                // connection, then skip the wire; i.e., you are at the end of the loop
                //
                toBlockTag = null;
                toPointTag = null;
              } else {
                if (toEquipmentBlockPointIndex !== -1) {
                  const foundToEquipmentBlockPoint = getNextValidEquipmentBlockPoint(
                    toEquipmentBlockPoints,
                    toEquipmentBlockPointIndex,
                    isToLast ? CableEnd.FROM : CableEnd.TO
                  );
                  if (foundToEquipmentBlockPoint) {
                    toBlockTag = foundToEquipmentBlockPoint.blockTag || null;
                    toPointTag = foundToEquipmentBlockPoint.pointTag || null;

                    // increment the term counter
                    if (isToLast) {
                      findAndIncrementTermCounter(
                        toEquipmentBlockStatMap,
                        toEquipmentId,
                        toBlockTag,
                        toPointTag,
                        TermBlockSide.CUSTOMER
                      );

                      if (isSameFromTo) {
                        findAndIncrementTermCounter(
                          fromEquipmemtBlockStatMap,
                          toEquipmentId,
                          toBlockTag,
                          toPointTag,
                          TermBlockSide.CUSTOMER
                        );
                      }
                    } else {
                      findAndIncrementTermCounter(
                        toEquipmentBlockStatMap,
                        toEquipmentId,
                        toBlockTag,
                        toPointTag,
                        TermBlockSide.VENDOR
                      );
                      if (isSameFromTo) {
                        findAndIncrementTermCounter(
                          fromEquipmemtBlockStatMap,
                          toEquipmentId,
                          toBlockTag,
                          toPointTag,
                          TermBlockSide.VENDOR
                        );
                      }
                    }
                    toEquipmentBlockPointIndex += 1;
                  } else {
                    toBlockTag = null;
                    toPointTag = null;
                  }
                } else {
                  toBlockTag = null;
                  toPointTag = null;
                }
              }
            }
          }
          //
          // FROM and TO terminations have be made so update term list
          //
          termItem.fromBlockTag = fromBlockTag;
          termItem.fromPointTag = fromPointTag;
          termItem.fromSide = TermBlockSide.CUSTOMER;
          //@ts-ignore set the term count to null though the type is number
          termItem.fromCustomerCount = null;
          //@ts-ignore set the term count to null though the type is number
          termItem.fromVendorCount = null;
          termItem.toBlockTag = toBlockTag;
          termItem.toPointTag = toPointTag;
          if (isToLast) {
            termItem.toSide = TermBlockSide.CUSTOMER;
          } else {
            termItem.toSide = TermBlockSide.VENDOR;
          }
          //@ts-ignore set the term count to null though the type is number
          termItem.toCustomerCount = null;
          //@ts-ignore set the term count to null though the type is number
          termItem.toVendorCount = null;
          changedTermIds.push(termItem.id);
        }
      }
    }
    setProgress(false);
    onAutoTerm(termListRef.current, changedTermIds);
  };

  return (
    <>
      <AutoTerminateModalStyled visible>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {({ values, setFieldValue, handleSubmit }) => {
            return (
              <>
                <ModalHeaderStyled title="">
                  <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-evenly' }}>
                    <Box pl={{ sm: '300' }}>
                      <MultiLingualText size="500" weight="500">
                        Auto-Termination for
                      </MultiLingualText>
                      &nbsp;
                      <Text size="500" weight="500">
                        {equipmentTag}
                      </Text>
                    </Box>
                    <ModalCloseStyled
                      onClick={() => {
                        onClose();
                      }}
                    >
                      <Button
                        size={ButtonSize.SMALL}
                        style={{ background: 'transparent', boxShadow: 'none' }}
                        onClick={() => close()}
                      >
                        <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
                      </Button>
                    </ModalCloseStyled>
                  </Box>
                </ModalHeaderStyled>
                {/* @ts-ignore adjust in GDS component to take child as prop  */}
                <ModalBody
                  css={`
                    flex-direction: column;
                  `}
                >
                  <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} px={{ sm: '500' }} py={{ sm: '500' }}>
                    <form style={{ width: '100%' }}>
                      <Box style={{ height: '160px' }}>
                        <Box pr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                          <Box flexDirection={{ sm: 'column' }}>
                            <FormSectionHeading heading="Wire ends to terminate" />
                          </Box>
                          <BoxContainerStyled>
                            <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                              <Box>
                                <ListSelectionStyled
                                  onClick={() => setFieldValue('autoTermOption', TermEnd.BOTH_FROM_AND_TO)}
                                >
                                  <Box mr={{ sm: '500' }}>
                                    <Radio
                                      size={TextFieldSize.MEDIUM}
                                      checked={values.autoTermOption === TermEnd.BOTH_FROM_AND_TO}
                                      readOnly={true}
                                    />
                                  </Box>
                                  <MultiLingualText size="300" color="grey.900">
                                    Both FROM and TO ends
                                  </MultiLingualText>
                                </ListSelectionStyled>
                              </Box>

                              <Box>
                                <ListSelectionStyled onClick={() => setFieldValue('autoTermOption', TermEnd.FROM_ONLY)}>
                                  <Box mr={{ sm: '500' }}>
                                    <Radio
                                      size={TextFieldSize.MEDIUM}
                                      checked={values.autoTermOption === TermEnd.FROM_ONLY}
                                      readOnly={true}
                                    />
                                  </Box>
                                  <MultiLingualText size="300" color="grey.900">
                                    FROM ends only
                                  </MultiLingualText>
                                </ListSelectionStyled>
                              </Box>

                              <Box>
                                <ListSelectionStyled onClick={() => setFieldValue('autoTermOption', TermEnd.TO_ONLY)}>
                                  <Box mr={{ sm: '500' }}>
                                    <Radio
                                      size={TextFieldSize.MEDIUM}
                                      checked={values.autoTermOption === TermEnd.TO_ONLY}
                                      readOnly={true}
                                    />
                                  </Box>
                                  <MultiLingualText size="300" color="grey.900">
                                    TO ends only
                                  </MultiLingualText>
                                </ListSelectionStyled>
                              </Box>
                            </Box>
                          </BoxContainerStyled>
                        </Box>
                        <Separator type={SeparatorType.VERTICAL} />
                        <Box px={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                          <Box flexDirection={{ sm: 'column' }}>
                            <FormSectionHeading heading="Shield Bar" />
                          </Box>
                          <BoxContainerStyled>
                            <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                              <Box>
                                <ListSelectionStyled
                                  onClick={() => setFieldValue('terminateToShieldBar', !values.terminateToShieldBar)}
                                >
                                  <Box mr={{ sm: '400' }}>
                                    <Checkbox
                                      checked={values.terminateToShieldBar ? true : false}
                                      size={TextFieldSize.MEDIUM}
                                      readOnly
                                    />
                                  </Box>
                                  <Box>
                                    <MultiLingualText color="grey.900" size="300">
                                      Terminate shield wires to shield bar
                                    </MultiLingualText>
                                  </Box>
                                </ListSelectionStyled>
                              </Box>
                              <Box mb={{ sm: '450' }}>
                                <FormField
                                  memberName="shieldBlockTag"
                                  metadataMemberName="blockTag"
                                  alwaysEditable={values.terminateToShieldBar}
                                >
                                  <TextFieldComponent />
                                </FormField>
                              </Box>
                              <Box mb={{ sm: '450' }}>
                                <FormField
                                  memberName="shieldPointTag"
                                  metadataMemberName="pointTag"
                                  alwaysEditable={values.terminateToShieldBar}
                                >
                                  <TextFieldComponent />
                                </FormField>
                              </Box>
                              <Box>
                                <ListSelectionStyled
                                  onClick={() =>
                                    setFieldValue(
                                      'termimateShieldWiresAtBothEnds',
                                      !values.termimateShieldWiresAtBothEnds
                                    )
                                  }
                                >
                                  <Box mr={{ sm: '400' }}>
                                    <Checkbox
                                      checked={values.termimateShieldWiresAtBothEnds ? true : false}
                                      size={TextFieldSize.MEDIUM}
                                      readOnly
                                    />
                                  </Box>
                                  <Box>
                                    <MultiLingualText color="grey.900" size="300">
                                      Apply to both ends
                                    </MultiLingualText>
                                  </Box>
                                </ListSelectionStyled>
                              </Box>
                            </Box>
                          </BoxContainerStyled>
                        </Box>
                        <Separator type={SeparatorType.VERTICAL} />
                        <Box px={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                          <Box flexDirection={{ sm: 'column' }}>
                            <FormSectionHeading heading="Ground Bar" />
                          </Box>
                          <BoxContainerStyled>
                            <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                              <Box>
                                <ListSelectionStyled
                                  onClick={() => setFieldValue('terminateToGroundBar', !values.terminateToGroundBar)}
                                >
                                  <Box mr={{ sm: '400' }}>
                                    <Checkbox
                                      checked={values.terminateToGroundBar ? true : false}
                                      size={TextFieldSize.MEDIUM}
                                      readOnly
                                    />
                                  </Box>
                                  <Box>
                                    <MultiLingualText color="grey.900" size="300">
                                      Terminate ground wires to ground bar
                                    </MultiLingualText>
                                  </Box>
                                </ListSelectionStyled>
                              </Box>
                              <Box mb={{ sm: '450' }}>
                                <FormField
                                  memberName="groundBlockTag"
                                  metadataMemberName="blockTag"
                                  alwaysEditable={values.terminateToGroundBar}
                                >
                                  <TextFieldComponent />
                                </FormField>
                              </Box>
                              <Box mb={{ sm: '450' }}>
                                <FormField
                                  memberName="groundPointTag"
                                  metadataMemberName="pointTag"
                                  alwaysEditable={values.terminateToGroundBar}
                                >
                                  <TextFieldComponent />
                                </FormField>
                              </Box>
                              <Box>
                                <ListSelectionStyled
                                  onClick={() =>
                                    setFieldValue(
                                      'termimategroundWiresAtBothEnds',
                                      !values.termimategroundWiresAtBothEnds
                                    )
                                  }
                                >
                                  <Box mr={{ sm: '400' }}>
                                    <Checkbox
                                      checked={values.termimategroundWiresAtBothEnds ? true : false}
                                      size={TextFieldSize.MEDIUM}
                                      readOnly
                                    />
                                  </Box>
                                  <Box>
                                    <MultiLingualText color="grey.900" size="300">
                                      Apply to both ends
                                    </MultiLingualText>
                                  </Box>
                                </ListSelectionStyled>
                              </Box>
                            </Box>
                          </BoxContainerStyled>
                        </Box>
                      </Box>
                    </form>
                    <Box flexGrow={{ sm: 1 }} mt={{ sm: '500' }}>
                      <Box flexGrow={{ sm: 1 }} mr={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
                        <Box flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '200' }}>
                            <MultiLingualText color="grey.800" size="400" weight="600">
                              Equipment and Cables
                            </MultiLingualText>
                          </Box>
                          <LineStyled style={{ width: '100%', marginBottom: '8px' }} />
                        </Box>
                        <Box flexGrow={{ sm: 1 }}>
                          <EquipmentCableList
                            items={equipmentCables}
                            onGridReady={handleGridReady}
                            gridApi={equipmentCableListGridApi}
                            rowDragManaged={true}
                            rowDraggableColumn="fromEquipmentTag"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box flexGrow={{ sm: 1 }} mt={{ sm: '200' }}>
                      <Box flexGrow={{ sm: 1 }} mr={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
                        <Box flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '200' }}>
                            <MultiLingualText color="grey.800" size="400" weight="600">
                              FROM Blocks
                            </MultiLingualText>
                          </Box>
                          <LineStyled style={{ width: '100%', marginBottom: '8px' }} />
                        </Box>
                        {fromEquipmentIds && fromEquipmentIds.length > 0 && (
                          <EquipmentBlockPointList
                            equipmentIds={fromEquipmentIds}
                            endType="FROM_EQUIPMENT"
                            onGridReady={handleFromBlockGridReady}
                            allowRowDrag={true}
                            rowDragManaged={true}
                            selectAllRows={true}
                            // blockPointListRef={fromBlockPointListRef}
                            equipmemtBlockStatMap={fromEquipmemtBlockStatMap}
                          ></EquipmentBlockPointList>
                        )}
                      </Box>
                      <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
                        <Box flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '200' }}>
                            <MultiLingualText color="grey.800" size="400" weight="600">
                              TO Blocks
                            </MultiLingualText>
                          </Box>
                          <LineStyled style={{ width: '100%', marginBottom: '8px' }} />
                        </Box>
                        {toEquipmentId && (
                          <EquipmentBlockPointList
                            equipmentIds={[toEquipmentId]}
                            endType="TO_EQUIPMENT"
                            onGridReady={handleToBlockGridReady}
                            allowRowDrag={true}
                            rowDragManaged={true}
                            selectAllRows={true}
                            equipmemtBlockStatMap={toEquipmentBlockStatMap}
                          ></EquipmentBlockPointList>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ModalBody>
                <ModalFooterStyled>
                  <Box p={{ sm: '500' }}>
                    <FooterButtonStyled onClick={() => handleSubmit()} disabled={progress}>
                      <Box px={{ sm: '500' }}>
                        {progress && <AppSpinner fillColor="white" />}
                        &nbsp;&nbsp;
                        <MultiLingualText>OK</MultiLingualText>
                      </Box>
                    </FooterButtonStyled>
                  </Box>
                </ModalFooterStyled>
              </>
            );
          }}
        </Formik>
      </AutoTerminateModalStyled>
    </>
  );
};
